import { useEffect } from 'react';

import { localStorage } from '@gitbook/util-local-storage';

/**
 * All the parameters that can be added to the App's url.
 */
export enum OnboardingParamName {
    OPENAPI = 'openapi',
    ORG_NAME = 'org_name',
}
export type OnboardingParams = {
    [OnboardingParamName.OPENAPI]: string | null;
    [OnboardingParamName.ORG_NAME]: string | null;
};

/**
 * We store parameters in local storage to preserve them across logins. This
 * is the key where we store the object.
 */
const STORAGE_KEY = 'onboardingParams';

/**
 * Private helper to get the whole object with all the parameters from local storage.
 */
function getOnboardingParams(): OnboardingParams | null {
    return localStorage.get<OnboardingParams>(STORAGE_KEY) ?? null;
}

/**
 * Helper function to get a specific parameter. Returns null if the parameter is not present.
 */
export function getOnboardingParam(name: OnboardingParamName): string | null {
    const params = getOnboardingParams();
    return params?.[name] ?? null;
}

/**
 * Helper function to clear a specific parameter.
 * Clears the whole object when the last parameter is removed.
 */
export function clearOnboardingParam(name: OnboardingParamName) {
    const currentParams = getOnboardingParams();

    // Exit early if already empty
    if (!getOnboardingParams()) {
        return; // Nothing to clear
    }

    // Remove the specific parameter
    const updatedParams = { ...currentParams };
    delete updatedParams[name];

    // If the resulting object is empty, remove the entire key
    if (Object.keys(updatedParams).length === 0) {
        localStorage.remove(STORAGE_KEY);
    } else {
        // Otherwise, update with the remaining params
        localStorage.set(STORAGE_KEY, updatedParams);
    }
}

/**
 * Helper function to parse the onboarding parameters from the URL.
 * Returns null if no parameters are present.
 */
export function parseOnboardingParamsFromURL(): OnboardingParams | null {
    const params = new URLSearchParams(window.location.search);
    const openapi = params.get(OnboardingParamName.OPENAPI);
    const orgName = params.get(OnboardingParamName.ORG_NAME);

    if (!openapi && !orgName) {
        return null;
    }

    return {
        [OnboardingParamName.OPENAPI]: openapi,
        [OnboardingParamName.ORG_NAME]: orgName,
    };
}

/**
 * Hook to persist the onboarding parameters from the URL to local storage.
 * To preserve them across logins.
 */
export function usePersistOnboardingParamsFromURL() {
    useEffect(() => {
        const onboardingParams = parseOnboardingParamsFromURL();

        if (onboardingParams) {
            localStorage.set(STORAGE_KEY, onboardingParams);
        }
    }, []);
}
